/**
 * Grid NPC AI
 * Adapts the NPC behavior system for tile-based combat with pathfinding.
 * NPCs pathfind to optimal positions, attack within range, and generate threats.
 */

import type { GridMap } from '@omen-quest/grid-map';
import {
  getReachableTiles, tileDistance, hasLineOfSight, coordKey,
} from '@omen-quest/grid-map';
import {
  type APCombatParticipant,
  type IncomingThreat,
  type ResolutionResult,
  AP_COSTS,
  SPELL_DEFINITIONS,
} from './types';
import type {
  GridCombatEngineState,
  TilePosition,
} from './grid-types';
import { getSkill } from './skills';
import { getGridCoverPenalty, getCellCombatEffects, getGridTileMoveCost } from './grid-terrain';

// ============================================================================
// BEHAVIOR TYPES (reused from npc-ai.ts)
// ============================================================================

export type NpcBehavior = 'aggressive' | 'defensive' | 'tactical' | 'support';

// ============================================================================
// BEHAVIOR DETERMINATION (reused logic from npc-ai.ts)
// ============================================================================

export function determineNpcBehavior(npc: APCombatParticipant): NpcBehavior {
  const tags = (npc.tags ?? []).map(t => t.toLowerCase());

  if (tags.some(t => ['aggressive', 'berserker', 'reckless', 'savage'].includes(t))) return 'aggressive';
  if (tags.some(t => ['defensive', 'guardian', 'protector', 'cautious'].includes(t))) return 'defensive';
  if (tags.some(t => ['tactical', 'cunning', 'strategic', 'clever'].includes(t))) return 'tactical';
  if (tags.some(t => ['healer', 'support', 'buffer', 'priest'].includes(t))) return 'support';

  const vitalsPercent = npc.vitals / npc.vitalsMax;
  if (vitalsPercent < 0.3) return 'defensive';
  if (vitalsPercent > 0.7) return 'aggressive';
  return 'tactical';
}

// ============================================================================
// CONDITION HELPERS
// ============================================================================

function hasCondition(p: APCombatParticipant, type: string): boolean {
  return p.conditions.some(c => c.startsWith(type + ':'));
}

function getTauntedBy(p: APCombatParticipant): string | null {
  const taunt = p.conditions.find(c => c.startsWith('taunted_by:'));
  if (!taunt) return null;
  return taunt.split(':')[1] || null;
}

// ============================================================================
// TARGET PRIORITIZATION
// ============================================================================

interface TargetPriority {
  targetId: string;
  priority: number;
  distance: number;
}

function prioritizeGridTargets(
  npc: APCombatParticipant,
  players: APCombatParticipant[],
  state: GridCombatEngineState,
  map: GridMap,
): TargetPriority[] {
  const npcPos = state.positions[npc.id];
  const tauntedBy = getTauntedBy(npc);

  const priorities: TargetPriority[] = [];

  for (const player of players) {
    if (player.isDefeated) continue;
    if (tauntedBy && player.id !== tauntedBy) continue;

    const playerPos = state.positions[player.id];
    const dist = tileDistance(npcPos.x, npcPos.y, playerPos.x, playerPos.y);

    let priority = 50;

    // Distance bonus: closer targets are higher priority
    if (dist <= 1) priority += 25;       // Adjacent
    else if (dist <= 3) priority += 15;  // Close
    else if (dist <= 5) priority += 8;   // Medium

    // Low vitals bonus: wounded targets are tempting
    const vitalsPercent = player.vitals / player.vitalsMax;
    if (vitalsPercent < 0.3) priority += 30;
    else if (vitalsPercent < 0.5) priority += 15;

    // Taunted target gets massive priority
    if (tauntedBy === player.id) priority += 100;

    // Cover penalty for ranged consideration
    if (dist > 1) {
      const cover = getGridCoverPenalty(map, playerPos.x, playerPos.y);
      priority -= cover * 8;
    }

    // LOS bonus
    if (hasLineOfSight(map, npcPos, playerPos)) {
      priority += 5;
    }

    priorities.push({ targetId: player.id, priority, distance: dist });
  }

  return priorities.sort((a, b) => b.priority - a.priority);
}

// ============================================================================
// POSITION SCORING
// ============================================================================

interface ScoredPosition {
  x: number;
  y: number;
  cost: number; // AP to reach
  path: TilePosition[];
  score: number;
}

function scorePositionForNpc(
  tile: { x: number; y: number; cost: number; path: TilePosition[] },
  npc: APCombatParticipant,
  primaryTarget: { id: string; pos: TilePosition } | null,
  behavior: NpcBehavior,
  state: GridCombatEngineState,
  map: GridMap,
  speed?: number,
): ScoredPosition {
  let score = 0;
  const dist = primaryTarget
    ? tileDistance(tile.x, tile.y, primaryTarget.pos.x, primaryTarget.pos.y)
    : 0;

  // Cover from walls
  const effects = getCellCombatEffects(map, tile.x, tile.y);
  const coverBonus = effects.coverLevel * 10;

  // LOS to target
  const hasLOS = primaryTarget
    ? hasLineOfSight(map, { x: tile.x, y: tile.y }, primaryTarget.pos)
    : false;

  switch (behavior) {
    case 'aggressive':
      // Minimize distance to target (get adjacent for melee)
      score = 100 - dist * 15;
      if (dist <= 1) score += 40; // Adjacent = melee range
      if (dist === 0) score += 20; // Same tile (shouldn't happen with 1-per-tile)
      score += coverBonus * 0.3; // Light cover consideration
      break;

    case 'defensive':
      // Maximize cover, stay at range, retreat if wounded
      score = coverBonus * 3;
      score += dist * 5; // Prefer distance
      if (hasLOS) score += 10; // But still want LOS for ranged
      if (npc.vitals / npc.vitalsMax < 0.3) score += dist * 8; // Extra retreat weight when hurt
      break;

    case 'tactical':
      // Seek cover with LOS to targets
      score = coverBonus * 2;
      if (hasLOS) score += 25;
      // Ideal range: close but not adjacent (2-3 tiles)
      if (dist >= 2 && dist <= 3) score += 20;
      else if (dist <= 1) score += 10; // Still OK to be adjacent
      else if (dist > 5) score -= 10; // Too far
      break;

    case 'support':
      // Stay near wounded allies
      const allies = state.participants.filter(p => p.type === npc.type && !p.isDefeated && p.id !== npc.id);
      if (allies.length > 0) {
        const woundedAlly = allies.reduce((worst, a) =>
          (a.vitals / a.vitalsMax) < (worst.vitals / worst.vitalsMax) ? a : worst
        );
        const allyPos = state.positions[woundedAlly.id];
        const allyDist = tileDistance(tile.x, tile.y, allyPos.x, allyPos.y);
        score += (10 - allyDist) * 5; // Close to wounded ally
      }
      score += coverBonus * 1.5;
      break;
  }

  // Penalty for movement cost (prefer closer tiles — measured in AP spent)
  const effectiveSpeed = speed ?? npc.speed;
  score -= Math.ceil(tile.cost / effectiveSpeed) * 2;

  return { x: tile.x, y: tile.y, cost: tile.cost, path: tile.path, score };
}

// ============================================================================
// NPC TURN EXECUTION
// ============================================================================

export interface EnemyTurnResult {
  results: ResolutionResult[];
  threats: Array<{ targetId: string; threat: IncomingThreat }>;
}

/**
 * Execute all enemy turns in initiative order.
 * Each enemy: move → attack/cast/skill → generate threats for player attacks.
 */
export function executeGridEnemyTurns(
  state: GridCombatEngineState,
  map: GridMap,
): EnemyTurnResult {
  const allResults: ResolutionResult[] = [];
  const allThreats: Array<{ targetId: string; threat: IncomingThreat }> = [];

  // Process enemies in turn order
  const enemyOrder = state.turnOrder.filter(id => {
    const p = state.participants.find(pp => pp.id === id);
    return p && p.type === 'npc' && !p.isDefeated;
  });

  for (const enemyId of enemyOrder) {
    const npc = state.participants.find(p => p.id === enemyId);
    if (!npc || npc.isDefeated) continue;

    // Skip stunned NPCs
    if (hasCondition(npc, 'stunned')) {
      allResults.push({
        actorId: npc.id, actorName: npc.name, actionType: 'stunned',
        effects: [`${npc.name} is stunned and cannot act!`],
      });
      continue;
    }

    const result = executeSingleEnemyTurn(npc, state, map);
    allResults.push(...result.results);
    allThreats.push(...result.threats);
  }

  return { results: allResults, threats: allThreats };
}

function executeSingleEnemyTurn(
  npc: APCombatParticipant,
  state: GridCombatEngineState,
  map: GridMap,
): EnemyTurnResult {
  const results: ResolutionResult[] = [];
  const threats: Array<{ targetId: string; threat: IncomingThreat }> = [];

  const behavior = determineNpcBehavior(npc);
  const players = state.participants.filter(p => p.type === 'player' && !p.isDefeated);
  if (players.length === 0) return { results, threats };

  // Prioritize targets
  const targetPriorities = prioritizeGridTargets(npc, players, state, map);
  if (targetPriorities.length === 0) return { results, threats };
  const primaryTarget = targetPriorities[0];
  const primaryTargetPos = state.positions[primaryTarget.targetId];

  let remainingAP = npc.apRemaining;

  // Reserve AP for reactions based on behavior
  const reactionReserve = Math.floor(remainingAP * getReactionReservePercent(behavior));
  const actionBudget = remainingAP - reactionReserve;

  // ---- STEP 1: Try casting a spell first (if has magic) ----
  if (npc.magicDiscipline && npc.magicDiscipline !== 'none' && actionBudget >= AP_COSTS.cast) {
    const spell = SPELL_DEFINITIONS[npc.magicDiscipline];
    if (npc.resolve >= spell.resolveCost) {
      const npcPos = state.positions[npc.id];
      const dist = tileDistance(npcPos.x, npcPos.y, primaryTargetPos.x, primaryTargetPos.y);
      const spellRange = spell.range === 'melee' ? 1 : spell.range === 'close' ? 3 : 10;
      const hasLOS = hasLineOfSight(map, npcPos, primaryTargetPos);

      if (dist <= spellRange && hasLOS) {
        // Cast the spell — generates a threat for offensive spells
        const target = state.participants.find(p => p.id === primaryTarget.targetId);
        if (target) {
          if (npc.magicDiscipline === 'vitality') {
            // Heal wounded ally
            const woundedAllies = state.participants.filter(
              p => p.type === npc.type && !p.isDefeated && p.vitals < p.vitalsMax
            ).sort((a, b) => a.vitals / a.vitalsMax - b.vitals / b.vitalsMax);
            const healTarget = woundedAllies[0] || npc;
            results.push({
              actorId: npc.id, actorName: npc.name, actionType: 'magic',
              targetId: healTarget.id, targetName: healTarget.name,
              effects: [`${npc.name} channels healing magic toward ${healTarget.name}.`],
            });
          } else {
            // Offensive spell → generate threat
            threats.push({
              targetId: primaryTarget.targetId,
              threat: {
                id: `${npc.id}_spell_${Math.random().toString(36).slice(2, 8)}`,
                attackerId: npc.id,
                attackerName: npc.name,
                actionId: spell.id,
                actionName: spell.name,
                apSpent: AP_COSTS.cast,
                estimatedDamage: 'moderate',
                canBeBlocked: false,
                canBeDodged: true,
                canBeParried: false,
              },
            });
            results.push({
              actorId: npc.id, actorName: npc.name, actionType: 'magic',
              targetId: target.id, targetName: target.name,
              effects: [`${npc.name} prepares ${spell.name} targeting ${target.name}!`],
            });
          }
          remainingAP -= AP_COSTS.cast;
          npc.resolve -= spell.resolveCost;
        }
      }
    }
  }

  // ---- STEP 2: Try using a skill ----
  const skillResult = evaluateNpcSkill(npc, behavior, state, map, Math.min(remainingAP, actionBudget));
  if (skillResult) {
    remainingAP -= skillResult.apCost;
    if (skillResult.generatesThreat && skillResult.targetId) {
      threats.push({
        targetId: skillResult.targetId,
        threat: {
          id: `${npc.id}_skill_${skillResult.skillId}_${Math.random().toString(36).slice(2, 8)}`,
          attackerId: npc.id,
          attackerName: npc.name,
          actionId: skillResult.skillId,
          actionName: skillResult.skillName,
          apSpent: skillResult.apCost,
          estimatedDamage: skillResult.estimatedDamage,
          canBeBlocked: skillResult.canBeBlocked,
          canBeDodged: skillResult.canBeDodged,
          canBeParried: skillResult.canBeParried,
        },
      });
    }
    results.push({
      actorId: npc.id, actorName: npc.name, actionType: 'skill',
      targetId: skillResult.targetId, targetName: skillResult.targetName,
      effects: [`${npc.name} uses ${skillResult.skillName}!`],
    });
    npc.resolve = Math.max(0, npc.resolve - skillResult.resolveCost);
    // Consume cooldown
    if (state.skillCooldowns[npc.id]?.[skillResult.skillId] !== undefined) {
      state.skillCooldowns[npc.id][skillResult.skillId] = Math.max(
        0, state.skillCooldowns[npc.id][skillResult.skillId] - 1
      );
    }
  }

  // ---- STEP 3: Move toward optimal position ----
  const moveBudgetAP = Math.min(remainingAP, actionBudget - (AP_COSTS.attack)); // Reserve at least attack AP
  if (moveBudgetAP >= 1) {
    const speed = npc.speed;
    const moveBudgetMP = moveBudgetAP * speed; // convert AP budget to movement points
    const npcPos = state.positions[npc.id];
    const occupied = getOccupiedTiles(state, npc.id);
    const reachable = getReachableTiles(map, npcPos, Math.max(0, moveBudgetMP), undefined, occupied, getGridTileMoveCost);

    if (reachable.size > 0) {
      // Score all reachable tiles
      const scoredTiles: ScoredPosition[] = [];
      for (const [, tile] of reachable) {
        const apCost = Math.ceil(tile.cost / speed);
        if (apCost > moveBudgetAP) continue; // can't afford
        const scored = scorePositionForNpc(
          tile, npc,
          { id: primaryTarget.targetId, pos: primaryTargetPos },
          behavior, state, map, speed,
        );
        scoredTiles.push(scored);
      }

      // Also score current position (cost 0, staying put)
      const currentScore = scorePositionForNpc(
        { x: npcPos.x, y: npcPos.y, cost: 0, path: [npcPos] },
        npc, { id: primaryTarget.targetId, pos: primaryTargetPos },
        behavior, state, map, speed,
      );

      // Pick best position
      const bestTile = scoredTiles.reduce((best, tile) =>
        tile.score > best.score ? tile : best, currentScore);

      if (bestTile.cost > 0 && (bestTile.x !== npcPos.x || bestTile.y !== npcPos.y)) {
        state.positions[npc.id] = { x: bestTile.x, y: bestTile.y };
        const apSpent = Math.ceil(bestTile.cost / speed);
        remainingAP -= apSpent;
        results.push({
          actorId: npc.id, actorName: npc.name, actionType: 'move',
          effects: [`${npc.name} moves to (${bestTile.x}, ${bestTile.y}).`],
        });
      }
    }
  }

  // ---- STEP 4: Attack with remaining AP ----
  if (remainingAP >= AP_COSTS.attack) {
    const npcPos = state.positions[npc.id];

    // Find adjacent enemies to attack
    for (const tp of targetPriorities) {
      if (remainingAP < AP_COSTS.attack) break;

      const targetPos = state.positions[tp.targetId];
      const dist = tileDistance(npcPos.x, npcPos.y, targetPos.x, targetPos.y);
      if (dist > 1) continue; // Not adjacent

      const target = state.participants.find(p => p.id === tp.targetId);
      if (!target || target.isDefeated) continue;

      // Generate attack threat (damage deferred until reaction phase)
      threats.push({
        targetId: tp.targetId,
        threat: {
          id: `${npc.id}_attack_${Math.random().toString(36).slice(2, 8)}`,
          attackerId: npc.id,
          attackerName: npc.name,
          actionId: 'attack',
          actionName: 'Attack',
          apSpent: AP_COSTS.attack,
          estimatedDamage: estimateDamageLevel(AP_COSTS.attack, npc.stats),
          canBeBlocked: true,
          canBeDodged: true,
          canBeParried: true,
        },
      });

      results.push({
        actorId: npc.id, actorName: npc.name, actionType: 'attack',
        targetId: target.id, targetName: target.name,
        effects: [`${npc.name} attacks ${target.name}!`],
      });

      remainingAP -= AP_COSTS.attack;
    }
  }

  return { results, threats };
}

// ============================================================================
// SKILL EVALUATION
// ============================================================================

interface SkillEvalResult {
  skillId: string;
  skillName: string;
  apCost: number;
  resolveCost: number;
  targetId?: string;
  targetName?: string;
  generatesThreat: boolean;
  estimatedDamage: 'light' | 'moderate' | 'heavy' | 'devastating';
  canBeBlocked: boolean;
  canBeDodged: boolean;
  canBeParried: boolean;
}

function evaluateNpcSkill(
  npc: APCombatParticipant,
  behavior: NpcBehavior,
  state: GridCombatEngineState,
  map: GridMap,
  availableAP: number,
): SkillEvalResult | null {
  const npcPos = state.positions[npc.id];
  let bestSkill: SkillEvalResult | null = null;
  let bestScore = -Infinity;

  for (const skillId of npc.skillIds) {
    const skill = getSkill(skillId);
    if (!skill) continue;
    if (skill.apCost > availableAP) continue;
    if (npc.resolve < skill.resolveCost) continue;

    // Cooldown check
    const uses = state.skillCooldowns[npc.id]?.[skillId];
    if (uses !== undefined && uses <= 0) continue;

    // Score by behavior
    let score = 0;
    switch (behavior) {
      case 'aggressive':
        score += skill.category === 'offensive' ? 30 : skill.category === 'movement' ? 15 : -10;
        break;
      case 'defensive':
        score += skill.category === 'defensive' ? 30 : skill.category === 'utility' ? 15 : -10;
        break;
      case 'tactical':
        score += skill.category === 'utility' ? 20 : skill.category === 'offensive' ? 15 : 10;
        break;
      case 'support':
        score += skill.category === 'utility' ? 30 : skill.category === 'defensive' ? 20 : -20;
        break;
    }

    // Situational bonuses
    if (skill.effects.some(e => e.type === 'heal')) {
      if (npc.vitals / npc.vitalsMax < 0.25) score += 40;
      else if (npc.vitals / npc.vitalsMax < 0.5) score += 20;
    }

    // Find target
    let targetId: string | undefined;
    let targetName: string | undefined;
    const enemies = state.participants.filter(p => p.type !== npc.type && !p.isDefeated);
    const allies = state.participants.filter(p => p.type === npc.type && !p.isDefeated);

    switch (skill.targeting) {
      case 'single_enemy': {
        // Find enemy in range
        for (const enemy of enemies) {
          const ePos = state.positions[enemy.id];
          const dist = tileDistance(npcPos.x, npcPos.y, ePos.x, ePos.y);
          const maxRange = skill.range === 'melee' ? 1 : skill.range === 'close' ? 3 : 10;
          if (dist > maxRange) continue;
          if (skill.range !== 'melee' && !hasLineOfSight(map, npcPos, ePos)) continue;
          targetId = enemy.id;
          targetName = enemy.name;
          break;
        }
        if (!targetId) continue; // No valid target
        break;
      }
      case 'single_ally': {
        const wounded = allies.filter(a => a.vitals < a.vitalsMax)
          .sort((a, b) => a.vitals / a.vitalsMax - b.vitals / b.vitalsMax);
        if (wounded.length > 0) {
          targetId = wounded[0].id;
          targetName = wounded[0].name;
        }
        break;
      }
      case 'self':
        targetId = npc.id;
        targetName = npc.name;
        break;
      case 'all_enemies':
      case 'all_allies':
        break; // AoE, no specific target needed
    }

    if (score > bestScore) {
      bestScore = score;
      const damageEffects = skill.effects.filter(
        (e): e is { type: 'damage'; baseDamage: number } => e.type === 'damage'
      );
      const totalDmg = damageEffects.reduce((sum, e) => sum + e.baseDamage, 0);

      bestSkill = {
        skillId: skill.id,
        skillName: skill.name,
        apCost: skill.apCost,
        resolveCost: skill.resolveCost,
        targetId,
        targetName,
        generatesThreat: skill.generatesThreat,
        estimatedDamage: totalDmg <= 1 ? 'light' : totalDmg <= 2 ? 'moderate' : totalDmg <= 3 ? 'heavy' : 'devastating',
        canBeBlocked: skill.threatProperties?.canBeBlocked ?? false,
        canBeDodged: skill.threatProperties?.canBeDodged ?? true,
        canBeParried: skill.threatProperties?.canBeParried ?? false,
      };
    }
  }

  return bestSkill;
}

// ============================================================================
// HELPERS
// ============================================================================

function getReactionReservePercent(behavior: NpcBehavior): number {
  switch (behavior) {
    case 'aggressive': return 0;
    case 'defensive': return 0.5;
    case 'tactical': return 0.3;
    case 'support': return 0.4;
  }
}

function estimateDamageLevel(
  apSpent: number,
  stats: { brawn: number },
): 'light' | 'moderate' | 'heavy' | 'devastating' {
  const pool = stats.brawn + Math.min(Math.max(0, apSpent - AP_COSTS.attack), 2);
  if (pool <= 2) return 'light';
  if (pool <= 4) return 'moderate';
  if (pool <= 6) return 'heavy';
  return 'devastating';
}

function getOccupiedTiles(state: GridCombatEngineState, excludeId: string): Set<string> {
  const occupied = new Set<string>();
  for (const p of state.participants) {
    if (p.isDefeated || p.id === excludeId) continue;
    const pos = state.positions[p.id];
    if (pos) occupied.add(coordKey(pos.x, pos.y));
  }
  return occupied;
}
